import React, { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import { RouterProvider } from "react-router";

import { getConfig } from "./config";
import { withFeatureFlagProvider } from "./feature-flags";
import { createRouter } from "./router";

import "@fontsource/poppins";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "@fontsource/poppins/800.css";

const App: React.FC = () => {
	useAdobeTagManager();

	const router = useMemo(() => createRouter(), []);

	return (
		<Suspense fallback={<LinearProgress />}>
			<RouterProvider router={router} />
		</Suspense>
	);
};

const useAdobeTagManager = () => {
	const isLoaded = useRef(false);
	const adobeTagManagerURL = getConfig("adobetagmanagerurl");

	const loadScript = useCallback(
		(onScriptLoad: () => void) => {
			const script = document.createElement("script");
			script.src = adobeTagManagerURL;
			script.async = true;
			script.addEventListener("load", onScriptLoad);
			document.body.appendChild(script);
			return () => {
				script.removeEventListener("load", onScriptLoad);
				document.body.removeChild(script);
			};
		},
		[adobeTagManagerURL],
	);

	useEffect(() => {
		if (!isLoaded.current) {
			return loadScript(() => {
				isLoaded.current = true;
			});
		}
		return () => {};
	}, [loadScript]);
};

export default withFeatureFlagProvider(App);
